import React from 'react'
import { useSelector } from 'hooks/use-redux'
import { getPermissions } from '@disco/state-management/selectors/user'
import RichTextEditor from 'components/common/rich-text-editor'
import Alert from 'components/ui/alert'
import InlineLink from 'components/ui/inline-link'
import Tooltip from 'components/ui/tooltip'
type Props = {
  invalid: boolean
  initialContent: Record<string, any> | null | undefined
  onChange: (...args: Array<any>) => void
}

/**
 * BusinessAddressField component for editing business address information
 *
 * This component provides a rich text editor for entering business address details,
 * which are required for anti-spam compliance when sending campaigns. Access is
 * restricted to business admins only.
 *
 * @param props - Component props
 * @param props.invalid - Whether the field has validation errors
 * @param props.initialContent - Initial content for the rich text editor
 * @param props.onChange - Callback function called when content changes
 * @returns JSX element containing the business address field
 */
const BusinessAddressField = ({
  invalid,
  initialContent,
  onChange,
}: Props): React.ReactElement<'div'> => {
  const isBusinessAdmin = useSelector(getPermissions).includes(
    'can_change_business_settings',
  )
  const empty = !initialContent
  const readOnly = !isBusinessAdmin
  let textEditor = (
    <RichTextEditor
      className="CampaignDetails_Input"
      initialContent={initialContent}
      invalid={invalid}
      onChange={onChange}
      readOnly={readOnly}
    />
  )

  if (readOnly) {
    textEditor = (
      // block: the activator is inline-block by default, which shrink-wraps
      // the editor to its text width instead of filling the form column
      <Tooltip
        block
        content="Only admins on this DISCO can update the business address"
        position="top"
      >
        {textEditor}
      </Tooltip>
    )
  }

  return (
    <div>
      {textEditor}
      {!isBusinessAdmin && empty && (
        <>
          <br />
          <Alert status="warning" icon="warning">
            To send this campaign, your account needs a business address listed.
            This is required for anti-spam laws. Unfortunately only admins on
            this DISCO can update the business address, and you are not an
            account admin. Please ask an account admin to add the address in{' '}
            <InlineLink to="/settings/business">Business Settings</InlineLink>.
          </Alert>
        </>
      )}
    </div>
  )
}

export default BusinessAddressField
